/**
 * Contratos do workspace de Estruturação do consultor (Onda 4 / A09) — espelhos dos DTOs
 * do A08 em `originei-oportunidades` e `originei-produtos`. Campo a campo, na mesma
 * ordem; um campo a mais aqui some silencioso na interface (lição do types/documento.ts).
 */
import { Oportunidade } from './oportunidade'
import type {
  DocumentoItemChecklist,
  HistoricoItemChecklist,
  OrigemItemChecklist,
  StatusArquivoChecklist,
  StatusItemChecklistAtual,
  UltimaDecisaoItem,
  VersaoChecklistReserva,
} from './checklist'

// ─── Diagnóstico (etapa 1) ───────────────────────────────────────────────────

/** Espelha `produtos/entity/TipoCampoDiagnostico` — decide o widget do campo. */
export type TipoCampoDiagnostico = 'TEXTO' | 'NUMERO' | 'MOEDA' | 'DATA' | 'OPCAO'

/** Espelha `produtos/dto/CampoDiagnosticoResponse`. */
export interface CampoDiagnostico {
  id: string
  produtoId: string
  ordem: number | null
  rotulo: string
  tipo: TipoCampoDiagnostico
  opcoes: string[]
  obrigatorio: boolean | null
  ativo: boolean | null
}

/**
 * Cadastro/edição de um campo (espelha `produtos/dto/CampoDiagnosticoRequest`) — ADMIN.
 *
 * `opcoes` só vale para OPCAO (o backend ignora nos demais tipos e recusa OPCAO sem
 * nenhuma alternativa). `obrigatorio`/`ativo` nulos são permissivos no backend: nulo =
 * não obrigatório, nulo = ativo.
 */
export interface CampoDiagnosticoInput {
  ordem?: number | null
  rotulo: string
  tipo: TipoCampoDiagnostico
  opcoes?: string[]
  obrigatorio?: boolean
  ativo?: boolean
}

/** Uma resposta CONFIRMADA (espelha `DiagnosticoReservaResponse.RespostaDiagnostico`). */
export interface RespostaDiagnostico {
  campoId: string
  valorTexto: string | null
  valorNumero: number | null
  /** ISO `YYYY-MM-DD`. */
  valorData: string | null
  atualizadoEm: string | null
  atualizadoPor: string | null
}

/** Contribuição da IA para um campo (bloco `estruturacao_sugerida`, JSON livre). */
export interface SugestaoCampoDiagnostico {
  valor: string | number
  /** 0..1 — quem exibe converte para porcentagem. */
  confianca?: number
  fonte?: string
  documentoId?: string
}

/** Bloco de sugestões — é SEMPRE proposta; nada vira resposta sem o usuário confirmar. */
export interface SugestoesDiagnostico {
  campos?: Record<string, SugestaoCampoDiagnostico>
  atualizadoEm?: string
}

/** Espelha `DiagnosticoReservaResponse`. */
export interface DiagnosticoReserva {
  oportunidadeId: string
  respostas: RespostaDiagnostico[]
  sugestoes: SugestoesDiagnostico | null
}

/**
 * Item do PUT (upsert). Conforme o tipo do campo, UM dos três valores é preenchido:
 * TEXTO/OPCAO → valorTexto; NUMERO/MOEDA → valorNumero; DATA → valorData.
 * Os três nulos = limpar a resposta daquele campo.
 */
export interface DiagnosticoRespostaInput {
  campoId: string
  valorTexto?: string | null
  valorNumero?: number | null
  valorData?: string | null
}

// ─── Checklist por categoria (etapa 3) ───────────────────────────────────────

/**
 * Espelha `oportunidades/entity/CategoriaDocumento` — enum LEGADO. Desde a P3 a categoria do
 * checklist da reserva é livre por versão (`codigo` + `rotulo` no grupo, `categoriaCodigo` +
 * `categoriaRotulo` no item); o enum continua só por compatibilidade (código fora dos 4 antigos
 * vem como CADASTRAL) e no DTO plano de `/documentos-obrigatorios`.
 */
export type CategoriaDocumento = 'CADASTRAL' | 'FINANCEIRA' | 'OPERACIONAL' | 'COMERCIAL'

export const CATEGORIA_DOCUMENTO_LABEL: Record<CategoriaDocumento, string> = {
  CADASTRAL: 'Cadastral',
  FINANCEIRA: 'Financeira',
  OPERACIONAL: 'Operacional',
  COMERCIAL: 'Comercial',
}

/** Ordem de exibição dos blocos — a do Figma (12:*). */
export const ORDEM_CATEGORIAS: CategoriaDocumento[] = [
  'CADASTRAL', 'FINANCEIRA', 'OPERACIONAL', 'COMERCIAL',
]

/**
 * Espelha `ChecklistReservaResponse.ItemChecklist` (AN-PP3 §6.4 / CONTRATO-PP3-O2 §1). Jackson
 * `non_null`: campo nulo vem OMITIDO — daí os opcionais. Tudo é calculado no backend (status,
 * validade, situação dos arquivos); a tela só exibe.
 */
export interface ItemChecklistReserva {
  /** P3: rótulo do item (o `label` continua por compatibilidade, com o mesmo texto). */
  rotulo?: string | null
  label: string
  tipo: string | null
  /** Enum LEGADO — para agrupar/rotular use `categoriaCodigo`/`categoriaRotulo`. */
  categoria?: CategoriaDocumento | null
  /** P3: categoria livre da versão presa (slug). */
  categoriaCodigo?: string | null
  categoriaRotulo?: string | null
  /** P3: VERSAO · SOLICITACAO_CURADORIA (`sol-N`) · LEGADO. */
  origem?: OrigemItemChecklist | null
  /** P3: dias de validade a partir da emissão (só itens com fonte aprovada — na v2, a matrícula). */
  validadeDias?: number | null
  /** P3: orientação ao consultor (o que enviar). */
  orientacao?: string | null
  /** P3: status atual do item (FALTANDO · QUARENTENA · RECEBIDO · VALIDADO · RECUSADO · VENCIDO). */
  status?: StatusItemChecklistAtual | null
  /** P3: status dos arquivos que seguram o item em QUARENTENA. */
  situacaoArquivos?: StatusArquivoChecklist[] | null
  /** P3: em VALIDADO/VENCIDO de item com validade, ISO `yyyy-MM-dd`. */
  validoAte?: string | null
  /** P3: cópia do reuso automático — só vem quando `true`; nenhum metadado de origem (Q42). */
  reaproveitado?: boolean | null
  /** P3: arquivos ATIVOS do item — o conjunto que o back-office valida. */
  documentos?: DocumentoItemChecklist[] | null
  /** P3: versões anteriores substituídas (status SUBSTITUIDO). */
  historico?: HistoricoItemChecklist[] | null
  /** P3: decisão mais recente; `autorNome` só para o back-office. */
  ultimaDecisao?: UltimaDecisaoItem | null
  /**
   * Documento vinculado a ESTA reserva — inclusive a cópia criada pelo reuso autorizado
   * (`POST /v1/documentos/reuso`). Documento de outra reserva do mesmo CNPJ não conta sem o
   * vínculo explícito (Q42).
   */
  presente: boolean
  /**
   * Identidade da exigência (V45). A tela a devolve no upload para dizer QUAL item o
   * arquivo satisfaz — sem ela o backend cai no tipo, que não distingue dois itens do
   * mesmo tipo. Nula nos itens legados por nome, que não têm cadastro.
   */
  chave: string | null
  /** Falso = desejável: não bloqueia o envio nem entra no percentual. */
  obrigatorio: boolean
  /** Documento da captura inicial (bloco de OCR). */
  basico: boolean
}

/**
 * Espelha `ChecklistReservaResponse` — percentual 0–100 já arredondado no backend.
 *
 * `total`, `presentes` e `percentual` contam só os itens OBRIGATÓRIOS: o header diz
 * "Documentação obrigatória", e um opcional que puxasse o percentual para baixo faria a
 * reserva parecer incompleta para sempre. Os opcionais vêm nos grupos, com
 * `obrigatorio: false`.
 */
export interface ChecklistReserva {
  /** P3: versão PRESA à reserva (P-10); ausente = reserva sem checklist versionado. */
  versao?: VersaoChecklistReserva | null
  total: number
  /** P3: obrigatórios em RECEBIDO ou VALIDADO. */
  entregues?: number
  /** P3: obrigatórios em VALIDADO (dentro da validade). */
  validados?: number
  /** Compatibilidade (= `entregues`). */
  presentes: number
  percentual: number
  categorias: GrupoChecklistReserva[]
}

/** Um grupo do checklist da reserva, na ordem da versão (inclui "Solicitados pela Curadoria"). */
export interface GrupoChecklistReserva {
  /** P3: categoria livre (string) da versão. */
  codigo?: string | null
  rotulo?: string | null
  /** Enum LEGADO. */
  categoria?: CategoriaDocumento | null
  itens: ItemChecklistReserva[]
}

// ─── Defesa de crédito (etapa 4) ─────────────────────────────────────────────

/** Espelha `oportunidades/entity/StatusDefesa` — nulo = defesa não iniciada. */
export type StatusDefesa = 'MINUTA' | 'EM_REVISAO' | 'APROVADA'

export const STATUS_DEFESA_LABEL: Record<StatusDefesa, string> = {
  MINUTA: 'Em preparação',
  EM_REVISAO: 'Em revisão',
  APROVADA: 'Aprovada',
}

/** Uma etapa da linha do tempo derivada (nunca inclui a minuta). */
export interface EtapaRastreabilidade {
  codigo: string
  rotulo: string
  concluida: boolean
  data: string | null
}

/** Espelha `DefesaRastreabilidadeResponse`. */
export interface DefesaRastreabilidade {
  oportunidadeId: string
  statusDefesa: StatusDefesa | null
  etapas: EtapaRastreabilidade[]
}

/** Campos do `OportunidadeResponse` que o A08 acrescentou e o types/oportunidade.ts não tem. */
export interface CamposEstruturacaoReserva {
  // As 7 colunas V39 (contato, segmento, ERP, contexto) ainda vêm na resposta, mas são
  // legado somente leitura (L6): a tela lê esses dados pelo formulário dinâmico, versão
  // LEGADO, e não os tipa aqui de propósito.
  statusDefesa?: StatusDefesa | null
  defesaAprovadaPor?: string | null
  defesaAprovadaEm?: string | null
  /** Status real na máquina de 18 estados — o detalhe passou a expor com o A08. */
  status?: import('./statusReserva').StatusReserva | null
}

/** A reserva como o workspace consome: o tipo existente + os campos da Onda 4. */
export type ReservaEstruturacao = Oportunidade & CamposEstruturacaoReserva

/**
 * Estado do GET /checklist na tela — separado da reserva porque ele é complemento:
 * falhar não derruba o workspace, mas também não pode virar "carregando" eterno.
 */
export type EstadoChecklist = 'carregando' | 'ok' | 'erro'
